import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { graphql, PageProps } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import {
  Button,
  Divider,
  Grid2 as Grid,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Language } from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  MarkdownContainer,
  ResponsiveGrid,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import { marketsConfig } from '../../../libs/config/markets';
import { getMarketDomain } from '../../../libs/config/markets-utils';
import { slugifyByLang } from '../../../libs/slugify';
import t from '../../../libs/translate';
import { NavigationData } from '../../components/header/types';
import { useShowMoreItems } from '../../helpers/use-show-more-items';
import { GlobalPageContext } from '../../types';
import { ContentSection } from '../dynamic-content-page/content-section';
import { formatDate } from '../press/helper';
import { ArticleStage } from './components/article-stage';
import {
  ArticleTeaserMedium,
  ShowMoreButtonContainer,
} from './components/article-teaser-medium';
import { AuthorBox } from './components/author-box';
import { addTableOfContentsHeadlines } from './components/content-section-selection/helper';
import { possibleComponents } from './components/content-section-selection/possible-components';
import { BlogArticleTranslations } from './components/content-section-selection/types';
import { SocialShare } from './components/social-share';
import { TableOfContents } from './components/table-of-contents';
import { Topics } from './components/topics';
import { StrapiBlogArticle } from './types';

export { Head } from '../../layouts/head';

type Props = NavigationData & {
  strapiBlogArticle: StrapiBlogArticle;
  translations: BlogArticleTranslations;
};

type PageContext = GlobalPageContext;

export const query = graphql`
  fragment BlogArticleFragment on STRAPI_BLOG_ARTICLE {
    title
    author {
      ...AuthorBoxFragment
    }
    excerpt
    date
    updatedAt
    blogTopics {
      topic
    }
    introduction {
      data {
        childMarkdownRemark {
          html
        }
      }
    }
    ...ContentSectionFragment

    responsiveCoverImage {
      ...StrapiImageFragment
    }

    coverImage {
      ...StrapiImageFileFragment
    }
  }

  query ($id: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    strapiBlogArticle(id: { eq: $id }) {
      ...BlogArticleFragment

      relatedArticles {
        ...BlogArticleFragment
      }
    }

    translations: translation(lang: { eq: $lang }) {
      ...ContentSectionTranslations
      article_topics
      blog_show_more_articles
      share_article
      blog_title
      blog_related_articles_headline
      blog_date_updated_at
    }
  }
`;

export const BlogArticle = ({
  data,
  location,
  pageContext,
}: PageProps<Props, PageContext>) => {
  return (
    <ArticleContainer
      data={data}
      pageContext={pageContext}
      pathName={location.pathname}
    />
  );
};

export const ArticleContainer = ({
  data: { strapiBlogArticle, translations, navigationLinks },
  pathName,
  pageContext: { langPrefix, lang, locale, market },
}: {
  data: Props;
  pathName: string;
  pageContext: Pick<PageContext, 'langPrefix' | 'lang' | 'locale' | 'market'>;
}) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const { itemCount, loadMoreItems } = useShowMoreItems({
    initialCount: 3,
    showMoreCount: 3,
  });
  const { activeSection } = useSectionObserver(sectionsRef, lang);
  const { isSticky } = useStickinessIndicator(isMobile, contentRef);

  const sections = useMemo(
    () => addTableOfContentsHeadlines(strapiBlogArticle.content ?? []),
    [strapiBlogArticle.content],
  );

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: navigationLinks.navigation.company.name,
      slug: `${langPrefix}/${navigationLinks.navigation.company.slug}/`,
    },
    {
      name: t(translations, 'blog_title'),
      slug: `${langPrefix}/${navigationLinks.blog.generalSlug.slug}/`,
    },
    {
      name: strapiBlogArticle.title,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: t(translations, 'blog_title'),
    slug: `${langPrefix}/${navigationLinks.blog.generalSlug.slug}/`,
  };

  const domain = getMarketDomain(market);
  const pageUrl = `${domain}${pathName}`;
  const authorName = strapiBlogArticle.author.name;
  const authorLinkAbsolute = `${domain}${langPrefix}/${navigationLinks.blog.generalSlug.slug}/${navigationLinks.blog.authorSlug.slug}/${slugifyByLang(authorName, lang)}/`;
  const authorImage = strapiBlogArticle.author.image;
  const coverImage = strapiBlogArticle.responsiveCoverImage;
  const mewaLogoUrl =
    'https://d1u1vozjf6xtm0.cloudfront.net/image/794933937620/image_cj9ebju4rp34p793921asii03i/-FPNG';
  const marketDetails = marketsConfig[market].languages.find(
    (language) => language.code === lang,
  );

  const jsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'BlogPosting',
    '@id': pageUrl,
    mainEntityOfPage: pageUrl,
    headline: strapiBlogArticle.title,
    name: strapiBlogArticle.title,
    description: strapiBlogArticle.introduction.data.childMarkdownRemark.html, // TODO: Strip html tags
    datePublished: new Date(strapiBlogArticle.date).toISOString(),
    dateModified: new Date(strapiBlogArticle.updatedAt).toISOString(),
    author: {
      '@type': 'Person',
      '@id': authorLinkAbsolute,
      name: authorName,
      url: authorLinkAbsolute,
      image: {
        '@type': 'ImageObject',
        '@id': authorImage.fields.assetUrl,
        url: authorImage.fields.assetUrl,
        width: authorImage.width,
        height: authorImage.height,
      },
    },
    publisher: {
      '@type': 'Organization',
      '@id': domain,
      name: marketDetails?.localizedMarketName,
      logo: {
        '@type': 'ImageObject',
        '@id': mewaLogoUrl,
        url: mewaLogoUrl,
        width: '590',
        height: '110',
      },
    },
    image: {
      '@type': 'ImageObject',
      '@id': coverImage.imageDesktop?.fields.assetUrl,
      url: coverImage.imageDesktop?.fields.assetUrl,
      width: coverImage.imageDesktop?.width,
      height: coverImage.imageDesktop?.height,
    },
    url: pageUrl,
    isPartOf: {
      '@type': 'Blog',
      '@id': `${domain}${langPrefix}/${navigationLinks.blog.generalSlug.slug}/`,
      name: 'Mewa Blog',
      publisher: {
        '@type': 'Organization',
        '@id': domain,
        name: marketDetails?.localizedMarketName,
      },
    },
    keywords: strapiBlogArticle.blogTopics.map((topic) => topic.topic),
  };

  return (
    <>
      <div
        data-pagefind-meta={`title:${strapiBlogArticle.title}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`author:${strapiBlogArticle.author.name}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`date:${strapiBlogArticle.date}`}
        style={{ visibility: 'hidden' }}
      />
      <StyledBreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <ArticleStage title={strapiBlogArticle.title} image={coverImage} />

      <ResponsiveGrid sx={{ marginTop: 'var(--spacer-xxxl)' }}>
        <Grid
          size={{ xs: 12, md: 10 }}
          offset={{ md: 1 }}
          sx={{ position: 'relative' }}
        >
          <Grid
            container
            columns={3}
            columnSpacing={{ xs: 'var(--gutter)' }}
            rowSpacing={0}
          >
            <ArticleMetaColumn
              size={{ xs: 3, sm: 1 }}
              sx={{ position: 'relative' }}
            >
              <ArticleDate>
                <strong>{formatDate(strapiBlogArticle.date, locale)}</strong>
                <br />
                {t(translations, 'blog_date_updated_at', {
                  date: formatDate(strapiBlogArticle.updatedAt, locale),
                })}
              </ArticleDate>
              <AuthorBox
                name={authorName}
                image={authorImage}
                position={strapiBlogArticle.author.position}
                authorLink={`${langPrefix}/${navigationLinks.blog.generalSlug.slug}/${navigationLinks.blog.authorSlug.slug}/${slugifyByLang(strapiBlogArticle.author.name, lang)}`}
                translations={translations}
              ></AuthorBox>
              {!isMobile ? (
                <StickyTableOfContents>
                  <TableOfContents
                    sections={sections}
                    activeSection={activeSection}
                  />
                </StickyTableOfContents>
              ) : null}
            </ArticleMetaColumn>

            <ContentContainer size={{ xs: 3, sm: 2 }} ref={contentRef}>
              {isMobile ? (
                <StickyTableOfContents className={isSticky ? 'sticky' : ''}>
                  <TableOfContents
                    sections={sections}
                    activeSection={activeSection}
                    isMobile
                  />
                </StickyTableOfContents>
              ) : null}
              <IntroductionMarkdownContainer
                html={
                  strapiBlogArticle.introduction.data.childMarkdownRemark.html
                }
              />
              <div
                ref={sectionsRef}
                className="ref"
                style={{ marginTop: 'var(--spacer-xl)' }}
              >
                <ContentSection
                  possibleComponents={possibleComponents}
                  sections={sections}
                  lang={lang}
                  langPrefix={langPrefix}
                  navigationLinks={navigationLinks}
                  locale={locale}
                  translations={translations}
                  conversion={{
                    link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
                    label: '',
                  }}
                />
              </div>
            </ContentContainer>
          </Grid>
        </Grid>

        <Grid size={12} sx={{ marginTop: 'var(--spacer-xxxl)' }}>
          <Divider />
        </Grid>

        <Grid
          size={{ xs: 12, md: 10 }}
          offset={{ md: 1 }}
          sx={{ marginTop: 'var(--spacer-xxxl)' }}
        >
          <Grid
            container
            columns={3}
            columnSpacing={'var(--gutter)'}
            rowSpacing={0}
          >
            <Grid size={{ xs: 3, sm: 1 }}>
              <FinishingSegmentHeadline>
                {t(translations, 'article_topics')}
              </FinishingSegmentHeadline>
              <Topics
                topics={strapiBlogArticle.blogTopics}
                baseUrl={`${langPrefix}/${navigationLinks.blog.generalSlug.slug}/${navigationLinks.blog.topicSlug.slug}`}
                lang={lang}
                alignment={'left'}
              ></Topics>
            </Grid>
            <Grid size={{ xs: 3, sm: 2 }}>
              <FinishingSegmentHeadline
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: {
                    marginTop: '15px',
                  },
                })}
              >
                {t(translations, 'share_article')}
              </FinishingSegmentHeadline>
              <SocialShare
                shareTitle={strapiBlogArticle.title}
                shareLink={`${localeBaseUrl(locale)}${langPrefix}/${navigationLinks.blog.generalSlug.slug}/${slugifyByLang(strapiBlogArticle.title, lang)}`}
              ></SocialShare>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12} sx={{ marginTop: 'var(--spacer-xxxl)' }}>
          <Divider />
        </Grid>

        <Grid
          size={{ xs: 12, md: 10 }}
          offset={{ md: 1 }}
          sx={{ marginTop: 'var(--spacer-xxxl)' }}
        >
          <Grid
            container
            columns={3}
            columnSpacing={'var(--gutter)'}
            rowSpacing={0}
          >
            <Grid size={3}>
              <FinishingSegmentHeadline>
                {t(translations, 'blog_related_articles_headline')}
              </FinishingSegmentHeadline>
            </Grid>
          </Grid>
          <Grid
            container
            columns={3}
            columnSpacing={'var(--gutter)'}
            rowSpacing={'var(--spacer-l)'}
          >
            {strapiBlogArticle.relatedArticles
              .slice(0, itemCount)
              .map((article) => (
                <StyledArticleItem size={{ xs: 3, sm: 1 }} key={article.title}>
                  <ArticleTeaserMedium
                    article={article}
                    lang={lang}
                    langPrefix={langPrefix}
                    blogSlug={navigationLinks.blog.generalSlug.slug}
                    locale={locale}
                    translations={translations}
                  />
                </StyledArticleItem>
              ))}
            {itemCount < strapiBlogArticle.relatedArticles.length && (
              <ShowMoreButtonContainer>
                <Button variant="text" onClick={loadMoreItems}>
                  {t(translations, 'blog_show_more_articles')}
                </Button>
              </ShowMoreButtonContainer>
            )}
          </Grid>
        </Grid>
      </ResponsiveGrid>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd, null, 2),
        }}
      />
    </>
  );
};

const useSectionObserver = (
  contentRef: React.RefObject<HTMLDivElement>,
  lang: Language,
) => {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    if (contentRef && contentRef.current) {
      Array.from(contentRef.current.children).forEach((element, idx) => {
        const observer = new IntersectionObserver(
          ([e]) => {
            if (e.isIntersecting) {
              setActiveSection(idx);
            }
          },
          { rootMargin: '-50px', threshold: [0.1] },
        );
        observer.observe(element);
      });
    }
  }, [lang, contentRef]);

  return { activeSection };
};

const useStickinessIndicator = (
  isMobile: boolean,
  contentWrapperRef: React.RefObject<HTMLDivElement>,
) => {
  const [isSticky, setIsSticky] = useState(false);

  const check = useCallback(() => {
    if (isMobile) {
      const rect = contentWrapperRef.current?.getBoundingClientRect();

      if ((rect?.top ?? 0) <= 0 && !isSticky) {
        setIsSticky(true);
      } else if ((rect?.top ?? 0) > 0 && isSticky) {
        setIsSticky(false);
      }
    }
  }, [isSticky, isMobile, contentWrapperRef]);

  useEffect(() => {
    window.addEventListener('scroll', check);

    return () => {
      window.removeEventListener('scroll', check);
    };
  }, [check]);

  return { isSticky };
};

const StyledBreadCrumbs = styled(BreadCrumbs)(({ theme }) => ({
  marginBottom: '2px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '9px',
    marginBottom: '9px',
  },
}));

const ContentContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    marginTop: '15px',
  },
}));

const StickyTableOfContents = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 'calc(var(--header-height) + var(--header-sticky-position) + var(--sticky-margin-top))',
  transition: 'top var(--transition-duration)',
  zIndex: 2,
  width: '100%',
  backgroundColor: 'var(--color-white)',
  padding: '0',
  margin: 0,
  '--sticky-margin-top': '0px',

  [theme.breakpoints.up('sm')]: {
    '--sticky-margin-top': '40px',
  },

  [theme.breakpoints.up('md')]: {
    '--sticky-margin-top': '50px',
  },

  [theme.breakpoints.up('lg')]: {
    '--sticky-margin-top': '60px',
  },
}));

const IntroductionMarkdownContainer = styled(MarkdownContainer)(
  ({ theme }) => ({
    marginBottom: '0 !important',

    [theme.breakpoints.down('sm')]: {
      marginTop: '30px',
    },

    '*, p': {
      ...theme.typography.h4,
      fontWeight: theme.typography.fontWeightMedium,
    },

    'p:first-child': {
      marginTop: 0,
    },

    'p:last-child': {
      marginBottom: 0,
    },
  }),
);

const FinishingSegmentHeadline = styled('h2')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: '0 0 20px 0',
}));

const ArticleDate = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightRegular,
  margin: '0 0 20px 0',

  '& strong': {
    fontWeight: theme.typography.fontWeightMedium,
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: '30px',
  },

  [theme.breakpoints.up('lg')]: {
    marginBottom: '40px',
  },
}));

const ArticleMetaColumn = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    paddingRight: 'var(--spacer-xxl)',
  },
}));

const StyledArticleItem = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    marginBottom: '40px',

    '&:last-child': {
      marginBottom: 0,
    },
  },
}));

BlogArticle.noPhoneContact = true;

export default BlogArticle;
